/*
 * NotificationDrawer.jsx
 * ----------------------
 * Slide-out panel from the right side showing all notifications.
 * Renders different icons per notification type.
 * Supports mark all read, individual mark as read, and dismiss.
 * Authenticated users only -- never rendered for guests.
 * Consumes NotificationContext.
 */

import React, { useEffect, useRef } from "react";
import { useNotifications } from "../App";

// ============================================
// NOTIFICATION TYPE CONFIG
// ============================================
const TYPE_CONFIG = {
  streak_milestone: { icon: "🔥", color: "var(--accent-gold)", label: "Streak" },
  checklist_complete: { icon: "✅", color: "var(--accent-green)", label: "Complete" },
  overdue: { icon: "⚠️", color: "var(--status-overdue)", label: "Overdue" },
  recurring_reset: { icon: "🔄", color: "var(--text-muted)", label: "Reset" },
  default: { icon: "🔔", color: "var(--text-muted)", label: "Notification" }
};

// ============================================
// SINGLE NOTIFICATION ROW
// ============================================
function NotificationRow({ notification, onMarkRead, onDismiss }) {
  const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.default;
  const isUnread = !notification.read;

  function formatTime(isoString) {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  return (
    <div
      onClick={() => !notification.read && onMarkRead(notification.id)}
      style={{
        display: "flex",
        gap: "var(--space-3)",
        padding: "var(--space-4)",
        background: isUnread ? "var(--bg-surface-2)" : "var(--bg-surface)",
        borderBottom: "1px solid var(--border)",
        cursor: isUnread ? "pointer" : "default",
        transition: "background var(--transition-fast)",
        position: "relative"
      }}
      onMouseEnter={e => {
        if (isUnread) e.currentTarget.style.background = "var(--accent-green-muted)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = isUnread
          ? "var(--bg-surface-2)"
          : "var(--bg-surface)";
      }}
    >
      {/* Unread indicator */}
      {isUnread && (
        <div style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "3px",
          background: config.color,
          borderRadius: "0 2px 2px 0"
        }} />
      )}

      {/* Icon */}
      <div style={{
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        background: "var(--bg-surface-2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px",
        flexShrink: 0,
        border: `1px solid var(--border)`
      }}>
        {config.icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontFamily: "var(--font-body)",
          fontWeight: isUnread ? 600 : 400,
          fontSize: "14px",
          color: "var(--text-primary)",
          margin: "0 0 2px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}>
          {notification.title}
        </p>
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "13px",
          color: "var(--text-secondary)",
          margin: "0 0 4px",
          lineHeight: 1.4
        }}>
          {notification.message}
        </p>
        <span style={{
          fontFamily: "var(--font-body)",
          fontSize: "11px",
          color: "var(--text-muted)"
        }}>
          {formatTime(notification.createdAt)}
        </span>
      </div>

      {/* Dismiss button */}
      <button
        onClick={e => { e.stopPropagation(); onDismiss(notification.id); }}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--text-muted)",
          padding: "var(--space-1)",
          borderRadius: "var(--radius-sm)",
          display: "flex",
          alignItems: "flex-start",
          flexShrink: 0,
          transition: "color var(--transition-fast)"
        }}
        onMouseEnter={e => e.currentTarget.style.color = "var(--text-primary)"}
        onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
}

// ============================================
// EMPTY STATE
// ============================================
function EmptyState() {
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "var(--space-8) var(--space-6)",
      gap: "var(--space-3)"
    }}>
      <div style={{ fontSize: "48px" }}>🔔</div>
      <p style={{
        fontFamily: "var(--font-body)",
        fontWeight: 600,
        fontSize: "16px",
        color: "var(--text-primary)",
        margin: 0
      }}>
        All caught up
      </p>
      <p style={{
        fontFamily: "var(--font-body)",
        fontSize: "14px",
        color: "var(--text-muted)",
        margin: 0,
        textAlign: "center"
      }}>
        Notifications for streaks, completions, and overdue items will appear here.
      </p>
    </div>
  );
}

// ============================================
// MAIN DRAWER EXPORT
// ============================================
export default function NotificationDrawer({ open, onClose }) {
  const {
    notifications,
    notificationsLoading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    dismissNotification
  } = useNotifications();

  const overlayRef = useRef(null);

  // Close on overlay click
  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) onClose();
  }

  // Close on Escape key
  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    if (open) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Backdrop overlay */}
      <div
        ref={overlayRef}
        onClick={handleOverlayClick}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 1100,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "all" : "none",
          transition: "opacity var(--transition-base)"
        }}
      />

      {/* Drawer panel */}
      <div style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: "min(400px, 100vw)",
        background: "var(--bg-surface)",
        zIndex: 1101,
        display: "flex",
        flexDirection: "column",
        boxShadow: "var(--shadow-lg)",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform var(--transition-slow)"
      }}>

        {/* Header */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "var(--space-5) var(--space-5) var(--space-4)",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0
        }}>
          <div>
            <h2 style={{
              fontFamily: "var(--font-display)",
              fontSize: "20px",
              fontWeight: 700,
              color: "var(--text-primary)",
              margin: "0 0 2px"
            }}>
              Notifications
            </h2>
            {unreadCount > 0 && (
              <p style={{
                fontFamily: "var(--font-body)",
                fontSize: "13px",
                color: "var(--text-muted)",
                margin: 0
              }}>
                {unreadCount} unread
              </p>
            )}
          </div>

          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            {/* Mark all read */}
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  background: "none",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: "var(--space-2) var(--space-3)",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontSize: "13px",
                  color: "var(--accent-green)",
                  fontWeight: 500,
                  transition: "background var(--transition-fast)"
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--accent-green-muted)"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                Mark all read
              </button>
            )}

            {/* Close button */}
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: "var(--space-2)",
                cursor: "pointer",
                color: "var(--text-secondary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background var(--transition-fast)"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--bg-surface-2)"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Notification list */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch"
        }}>
          {notificationsLoading ? (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "var(--space-8)"
            }}>
              <p style={{
                fontFamily: "var(--font-body)",
                color: "var(--text-muted)",
                fontSize: "14px"
              }}>
                Loading...
              </p>
            </div>
          ) : notifications.length === 0 ? (
            <EmptyState />
          ) : (
            notifications.map(notification => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onMarkRead={markAsRead}
                onDismiss={dismissNotification}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div style={{
            padding: "var(--space-4) var(--space-5)",
            borderTop: "1px solid var(--border)",
            flexShrink: 0
          }}>
            <p style={{
              fontFamily: "var(--font-body)",
              fontSize: "12px",
              color: "var(--text-muted)",
              margin: 0,
              textAlign: "center"
            }}>
              Notifications are kept for 30 days
            </p>
          </div>
        )}
      </div>
    </>
  );
}